"use client";

import { useEffect, useState } from "react";
import type { SidebarLabel } from "./sidebar";
import CreateGame from "./main/createGame";
import CreateSequence from "./main/createSequence";
import PilaThailandLibrary from "./main/pilaThailandLibrary";
import Situation from "./games/situation/situation";
import Sequence from "./sequence/sequence";
import FindTheMatch from "./games/findTheMatch/findTheMatch";
import GameShowQuiz from "./games/gameShowQuiz/gameShowQuiz";
import Anagram  from "./games/anagram/anagram";
import CreateGamePopUp from "./popup/createGamePopUp";
import mainAPI from "../services/main/mainAPI";
import WhackAMole from "./games/whackAMole/whackAMole";
import FlipCards from "./games/flipCards/flipCards";
import AirPlanFlyingFruits from "./games/flyingFruits/flyingFruits";
import FlappyBird from "./games/flappyBird-AirPlane/flappyBird-AirPlane";
import CompleteTheSentence from "./games/completeTheSentence/completeTheSentence";

type MainProps = {
    selectedLabel: SidebarLabel;
    onTemplateEditorVisibilityChange?: (isVisible: boolean) => void;
    onNavigateToLabel?: (label: SidebarLabel) => void;
};

type CreateTab = "create-game" | "create-sequence";

type CreatedGameData = {
    id: number | string;
    exercise_name?: string;
    uuid?: string;
    description?: string;
};

type GameInfoWithQuestionsResponse = {
    game_info?: {
        id?: number | string;
        uuid?: string;
        exercise_name?: string;
        description?: string | null;
        [key: string]: unknown;
    };
    questions?: unknown[];
};

export default function Main({ selectedLabel, onTemplateEditorVisibilityChange, onNavigateToLabel }: MainProps) {
    const [createTab, setCreateTab] = useState<CreateTab>("create-game");
    const [selectedTemplateTitle, setSelectedTemplateTitle] = useState<string | null>(null);
    const [activeTemplateEditorTitle, setActiveTemplateEditorTitle] = useState<string | null>(null);
    const [createdGameData, setCreatedGameData] = useState<CreatedGameData | null>(null);
    const [loadedGameDetail, setLoadedGameDetail] = useState<GameInfoWithQuestionsResponse | null>(null);

    useEffect(() => {
        if (selectedLabel !== "สร้างแบบฝึกหัดใหม่") {
            setCreateTab("create-game");
            setSelectedTemplateTitle(null);
            setActiveTemplateEditorTitle(null);
            setCreatedGameData(null);
            setLoadedGameDetail(null);
        }
    }, [selectedLabel]);

    useEffect(() => {
        const isTemplateEditorVisible =
            selectedLabel === "สร้างแบบฝึกหัดใหม่" && activeTemplateEditorTitle !== null;
        onTemplateEditorVisibilityChange?.(isTemplateEditorVisible);
    }, [activeTemplateEditorTitle, selectedLabel, onTemplateEditorVisibilityChange]);

    const openCreatePopUp = (title: string) => {
        setSelectedTemplateTitle(title);
    };

    const closeCreatePopUp = () => {
        setSelectedTemplateTitle(null);
    };

    const confirmCreatePopUp = (gameData: CreatedGameData) => {
        if (selectedTemplateTitle) {
            setActiveTemplateEditorTitle(selectedTemplateTitle);
        }

        setLoadedGameDetail(null);
        setCreatedGameData(gameData);

        setSelectedTemplateTitle(null);
    };

    const handleCompleted = () => {
        setCreateTab("create-game");
        setSelectedTemplateTitle(null);
        setActiveTemplateEditorTitle(null);
        setCreatedGameData(null);
        setLoadedGameDetail(null);
        onNavigateToLabel?.("คลังบทเรียนของคุณ");
    };

    const handleOpenLibraryRecord = async (record: {
        uuid: string;
        gameType: string;
        source: string;
        gameId: number | null;
        title: string;
    }) => {
        const source = String(record.source ?? "").toLowerCase();

        if (source === "sequence") {
            setActiveTemplateEditorTitle("Sequence");
            setLoadedGameDetail(null);
            setCreatedGameData({
                id: record.gameId ?? record.uuid,
                uuid: String(record.uuid ?? ""),
                exercise_name: String(record.title ?? ""),
                description: "",
            });
            onNavigateToLabel?.("สร้างแบบฝึกหัดใหม่");
            return;
        }

        const response = await mainAPI.getGameinfoAndQuestionByUuid(record.uuid);
        if (!response || (response as { status?: string }).status === "failed") {
            return;
        }

        const parsed = response as GameInfoWithQuestionsResponse;
        const gameInfo = parsed.game_info;

        const gameTypeKey = String(record.gameType ?? "").toLowerCase();
        const editorTitle =
            gameTypeKey === "find_the_match"
                ? "Find the match"
                : gameTypeKey === "anagram"
                    ? "Anagram"
                    : gameTypeKey === "game_show_quiz"
                        ? "Game show Quiz"
                        : gameTypeKey === "whack_a_mole"
                            ? "Whack a mole"
                            : gameTypeKey === "air_plan/flying_fruits"
                                ? "Air plan Flying Fruits"
                                : gameTypeKey === "flip_cards"
                                    ? "Flip cards"
                                    : gameTypeKey === "flappy_bird"
                                        ? "Flappy Bird"
                                        : gameTypeKey === "complete_the_sentence"
                                            ? "Complete the Sentence"
                                            : "Situation";

        setActiveTemplateEditorTitle(editorTitle);
        setLoadedGameDetail(parsed);

        if (gameInfo?.id !== undefined) {
            setCreatedGameData({
                id: gameInfo.id,
                uuid: String(gameInfo.uuid ?? record.uuid ?? ""),
                exercise_name: String(gameInfo.exercise_name ?? ""),
                description: String(gameInfo.description ?? ""),
            });
        } else {
            setCreatedGameData({
                id: record.gameId ?? record.uuid,
                uuid: String(record.uuid ?? ""),
                exercise_name: String(record.title ?? ""),
                description: "",
            });
        }

        onNavigateToLabel?.("สร้างแบบฝึกหัดใหม่");
    };

    return (
        <main className="flex-1 bg-[#F4F2E5] p-3 sm:p-4">
            {selectedLabel === "คลังบทเรียนต้นแบบ" ? (
                <PilaThailandLibrary mode="template" onOpenRecord={handleOpenLibraryRecord} />
            ) : selectedLabel === "คลังบทเรียนของคุณ" ? (
                <PilaThailandLibrary mode="my-library" onOpenRecord={handleOpenLibraryRecord} />
            ) : selectedLabel === "สร้างแบบฝึกหัดใหม่" ? (
                <section className="h-full rounded-lg bg-[#ffffff] shadow-[inset_0_0_0_1px_rgba(0,0,0,0.04)]">
                    {!activeTemplateEditorTitle ? (
                        <div className="flex items-end bg-[#F4F2E5]">
                            <button
                                type="button"
                                onClick={() => setCreateTab("create-game")}
                                className={`min-w-43 border-r border-[#d8d8d8] px-10 py-3 text-xl font-bold leading-none cursor-pointer rounded-tl-xl ${createTab === "create-game"
                                        ? "bg-[#ffffff] text-[#2B2B2B]"
                                        : "bg-[#f5e648] text-[#2B2B2B]"
                                    }`}
                            >
                                Create Game
                            </button>
                            <button
                                type="button"
                                onClick={() => setCreateTab("create-sequence")}
                                className={`min-w-43 px-10 py-3 text-xl font-bold leading-none cursor-pointer rounded-tr-xl ${createTab === "create-sequence"
                                        ? "bg-[#ffffff] text-[#2B2B2B]"
                                        : "bg-[#f5e648] text-[#2B2B2B]"
                                    }`}
                            >
                                Create Sequence
                            </button>
                        </div>
                    ) : null}

                    <div className="p-6">
                        {activeTemplateEditorTitle === "Situation" ? (
                            <Situation
                                createdGameData={createdGameData}
                                initialGameDetail={loadedGameDetail}
                                onCompleted={handleCompleted}
                            />
                        ) : activeTemplateEditorTitle === "Find the match" ? (
                            <FindTheMatch
                                createdGameData={createdGameData}
                                initialGameDetail={loadedGameDetail}
                                onCompleted={handleCompleted}
                            />
                        ) : activeTemplateEditorTitle === "Game show Quiz" ? (
                            <GameShowQuiz
                                createdGameData={createdGameData}
                                initialGameDetail={loadedGameDetail}
                                onCompleted={handleCompleted}
                            />
                        ) : activeTemplateEditorTitle === "Anagram" ? (
                            <Anagram
                                createdGameData={createdGameData}
                                initialGameDetail={loadedGameDetail}
                                onCompleted={handleCompleted}
                            />
                        ) : activeTemplateEditorTitle === "Sequence" ? (
                            <Sequence createdGameData={createdGameData} />
                        ) : activeTemplateEditorTitle === "Whack a mole" ? (
                            <WhackAMole
                                createdGameData={createdGameData}
                                initialGameDetail={loadedGameDetail}
                                onCompleted={handleCompleted}
                            />
                        ) : activeTemplateEditorTitle === "Flip cards" ? (
                            <FlipCards
                                createdGameData={createdGameData}
                                initialGameDetail={loadedGameDetail}
                                onCompleted={handleCompleted}
                            />
                        ) : activeTemplateEditorTitle === "Air plan Flying Fruits" ? (
                            <AirPlanFlyingFruits
                                createdGameData={createdGameData}
                                initialGameDetail={loadedGameDetail}
                                onCompleted={handleCompleted}
                            />
                        ) : activeTemplateEditorTitle === "Flappy Bird" ? (
                            <FlappyBird
                                createdGameData={createdGameData}
                                initialGameDetail={loadedGameDetail}
                                onCompleted={handleCompleted}
                            />
                        ) : activeTemplateEditorTitle === "Complete the Sentence" ? (
                            <CompleteTheSentence
                                createdGameData={createdGameData}
                                initialGameDetail={loadedGameDetail}
                                onCompleted={handleCompleted}
                            />
                        ) : activeTemplateEditorTitle ? (
                            <section className="flex min-h-100 items-center justify-center rounded border border-[#d7d7d7] bg-[#f5f5f5] p-6 text-center text-[#616161]">
                                โหมดแก้ไขสำหรับ {activeTemplateEditorTitle} กำลังพัฒนา
                            </section>
                        ) : createTab === "create-game" ? (
                            <CreateGame onSelectTemplate={openCreatePopUp} />
                        ) : (
                            <CreateSequence onSelectTemplate={openCreatePopUp} />
                        )}
                    </div>
                </section>
            ) : (
                <section className="flex h-full items-center justify-center rounded-lg bg-[#ffffff] p-6 text-lg text-neutral-400 shadow-[inset_0_0_0_1px_rgba(0,0,0,0.04)]">
                    ยังไม่เปิดใช้งานหน้านี้
                </section>
            )}

			{selectedTemplateTitle ? (
                <CreateGamePopUp title={selectedTemplateTitle} onClose={closeCreatePopUp} onConfirm={confirmCreatePopUp} />
			) : null}
        </main>
    );
}
